import { useContext, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import RoleCard from './role-card';
import './styles.scss';
import {
  AccountBalanceWalletOutlined,
  AdminPanelSettingsOutlined,
  CallToActionOutlined,
  CodeOutlined,
  PersonOutline,
  Visibility,
} from '@mui/icons-material';
import l from '../../../../../lang';
import Parser from 'html-react-parser';
import { Button } from '../../../../common/button';
import MessageError from '../../../../common/message-error';
import { InvitationActionType, InvitationContext, Role } from '../invitation-context';
import { useUserManagement } from '../../../../../store/user-management';

interface DialogInviteStep2Props {
  onNext: () => void;
  onBack: () => void;
}

const roles = [
  {
    id: 'ROLE_OWNER',
    disabled: true,
    title: l('roles.ROLE_OWNER'),
    subtitle: l('userManagement.step2.ROLE_OWNER.subtitle'),
    description: l('userManagement.step2.ROLE_OWNER.description'),
    canDo: [{ text: l('userManagement.step2.ROLE_OWNER.canDo.0') }, { text: l('userManagement.step2.ROLE_OWNER.canDo.1') }],
    cannotDo: [
      { text: l('userManagement.step2.ROLE_OWNER.cannotDo.0') },
      { text: l('userManagement.step2.ROLE_OWNER.cannotDo.1') },
    ],
    icon: <PersonOutline sx={{ fontSize: 36 }} />,
    tooltip: l('userManagement.step2.ROLE_OWNER.tooltip'),
  },
  {
    id: 'ROLE_ADMINISTRATOR',
    title: l('roles.ROLE_ADMINISTRATOR'),
    subtitle: l('userManagement.step2.ROLE_ADMINISTRATOR.subtitle'),
    description: l('userManagement.step2.ROLE_ADMINISTRATOR.description'),
    canDo: [
      { text: l('userManagement.step2.ROLE_ADMINISTRATOR.canDo.0') },
      { text: l('userManagement.step2.ROLE_ADMINISTRATOR.canDo.1') },
      { text: l('userManagement.step2.ROLE_ADMINISTRATOR.canDo.2') },
    ],
    cannotDo: [{ text: l('userManagement.step2.ROLE_ADMINISTRATOR.cannotDo.0') }],
    icon: <AdminPanelSettingsOutlined sx={{ fontSize: 34 }} />,
    tooltip: l('userManagement.step2.ROLE_ADMINISTRATOR.tooltip'),
  },
  {
    id: 'ROLE_FINANCE',
    title: l('roles.ROLE_FINANCE'),
    subtitle: l('userManagement.step2.ROLE_FINANCE.subtitle'),
    description: l('userManagement.step2.ROLE_FINANCE.description'),
    canDo: [
      { text: l('userManagement.step2.ROLE_FINANCE.canDo.0') },
      { text: l('userManagement.step2.ROLE_FINANCE.canDo.1') },
    ],
    cannotDo: [
      { text: l('userManagement.step2.ROLE_FINANCE.cannotDo.0') },
      { text: l('userManagement.step2.ROLE_FINANCE.cannotDo.1') },
    ],
    icon: <AccountBalanceWalletOutlined sx={{ fontSize: 36 }} />,
    tooltip: l('userManagement.step2.ROLE_FINANCE.tooltip'),
  },
  {
    id: 'ROLE_SALES_CS',
    title: l('roles.ROLE_SALES_CS'),
    subtitle: l('userManagement.step2.ROLE_SALES_CS.subtitle'),
    description: l('userManagement.step2.ROLE_SALES_CS.description'),
    canDo: [
      { text: l('userManagement.step2.ROLE_SALES_CS.canDo.0') },
      { text: l('userManagement.step2.ROLE_SALES_CS.canDo.1') },
    ],
    cannotDo: [
      { text: l('userManagement.step2.ROLE_SALES_CS.cannotDo.0') },
      { text: l('userManagement.step2.ROLE_SALES_CS.cannotDo.1') },
    ],
    icon: <CallToActionOutlined sx={{ fontSize: 36 }} />,
    tooltip: l('userManagement.step2.ROLE_SALES_CS.tooltip'),
  },
  {
    id: 'ROLE_TECHNICAL',
    title: l('roles.ROLE_TECHNICAL'),
    subtitle: l('userManagement.step2.ROLE_TECHNICAL.subtitle'),
    description: l('userManagement.step2.ROLE_TECHNICAL.description'),
    canDo: [
      { text: l('userManagement.step2.ROLE_TECHNICAL.canDo.0') },
      { text: l('userManagement.step2.ROLE_TECHNICAL.canDo.1') },
    ],
    cannotDo: [{ text: l('userManagement.step2.ROLE_TECHNICAL.cannotDo.0') }],
    icon: <CodeOutlined sx={{ fontSize: 36 }} />,
    tooltip: l('userManagement.step2.ROLE_TECHNICAL.tooltip'),
  },
  {
    id: 'ROLE_READ_ONLY',
    title: l('roles.ROLE_READ_ONLY'),
    subtitle: l('userManagement.step2.ROLE_READ_ONLY.subtitle'),
    description: l('userManagement.step2.ROLE_READ_ONLY.description'),
    canDo: [{ text: l('userManagement.step2.ROLE_READ_ONLY.canDo.0') }],
    cannotDo: [{ text: l('userManagement.step2.ROLE_READ_ONLY.cannotDo.0') }],
    icon: <Visibility sx={{ fontSize: 36 }} />,
    tooltip: l('userManagement.step2.ROLE_READ_ONLY.tooltip'),
  },
];

const footerMessages = {
  ROLE_OWNER: 'userManagement.step2.footerMsg.ROLE_OWNER',
  ROLE_ADMINISTRATOR: 'userManagement.step2.footerMsg.ROLE_ADMINISTRATOR',
  ROLE_FINANCE: 'userManagement.step2.footerMsg.ROLE_FINANCE',
  ROLE_SALES_CS: 'userManagement.step2.footerMsg.ROLE_SALES_CS',
  ROLE_TECHNICAL: 'userManagement.step2.footerMsg.ROLE_TECHNICAL',
  ROLE_READ_ONLY: 'userManagement.step2.footerMsg.ROLE_READ_ONLY',
};

const DialogInviteStep2 = ({ onNext, onBack }: DialogInviteStep2Props) => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const { state, dispatch: invitationDispatch } = useContext(InvitationContext);
  const {
    inviteUser,
    inviteLoading,
    inviteError,
    inviteErrorInfo,
    cleanError,
  } = useUserManagement();

  useEffect(() => {
    cleanError();
  }, []);
  const reduxDispatch = useDispatch();

  const handleSendInvitation = () => {
    if (!selectedRole) return;
    if (!state.email || state.email.trim() === '') {
      return;
    }
    if (!state.fullName || state.fullName.trim() === '') {
      return;
    }
    invitationDispatch({
      type: InvitationActionType.SET_ROLE,
      payload: {
        role: selectedRole as Role,
      },
    });
    cleanError();
    inviteUser(state.fullName, state.email, selectedRole);
  };


  const prevInviteLoading = useRef(inviteLoading);
  useEffect(() => {
    if (prevInviteLoading.current && !inviteLoading && !inviteError) {
      onNext();
    }
    prevInviteLoading.current = inviteLoading;
  }, [inviteLoading, inviteError, onNext]);

  return (
    <div className="step2_container">
      <h3 className="step2_container_title">{l('userManagement.step2.title')}</h3>
      <div className="step2_container_roles">
        {roles.map(role => (
          <RoleCard
            key={role.id}
            id={role.id}
            disabled={role.disabled}
            title={role.title}
            subtitle={role.subtitle}
            description={role.description}
            canDo={role.canDo}
            cannotDo={role.cannotDo}
            icon={role.icon}
            selected={selectedRole === role.id}
            onSelect={id => setSelectedRole(id)}
            tooltip={role.tooltip}
          />
        ))}
      </div>
      <div className="step2_container_footer">
        {selectedRole && (
          <div className="step2_container_footer_content">
            {Parser(l(footerMessages[selectedRole as keyof typeof footerMessages], state.fullName))}
          </div>
        )}
        {inviteError && inviteErrorInfo && (
          <div className="step2_error-message">
            <MessageError
              errorInfo={{
                ...inviteErrorInfo,
                message:
                  inviteErrorInfo.body && inviteErrorInfo.body.errorCode === 204
                    ? l('msgError.signUp.emailAlreadyRegistered')
                    : (inviteErrorInfo.body && 'errorMessage' in inviteErrorInfo.body && inviteErrorInfo.body.errorMessage
                        ? inviteErrorInfo.body.errorMessage
                        : l('genericError.message')),
              }}
              onClose={cleanError}
            />
          </div>
        )}
        <div className="step2_container_footer_buttons">
          <Button variant="outlined" onClick={onBack}>{l('common.return')}</Button>
          <Button variant="contained" color="primary" onClick={handleSendInvitation} disabled={!selectedRole || inviteLoading}>
            {l('userManagement.step2.sendInvitation')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DialogInviteStep2;
